import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Search, Pencil, Trash2, Plus } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

interface SavedTag {
  id: string;
  tag_name: string;
  color: string;
  usage_count: number;
}

interface TagManagementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unaId?: string;
}

const TAG_COLORS = [
  { value: "red", label: "🔴 Red" },
  { value: "blue", label: "🔵 Blue" },
  { value: "green", label: "🟢 Green" },
  { value: "yellow", label: "🟡 Yellow" },
  { value: "purple", label: "🟣 Purple" },
  { value: "orange", label: "🟠 Orange" },
  { value: "gray", label: "⚪ Gray" },
];

export const TagManagementModal = ({ open, onOpenChange, unaId }: TagManagementModalProps) => {
  const { user } = useAuth();
  const [tags, setTags] = useState<SavedTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedTag, setSelectedTag] = useState<SavedTag | null>(null);
  const [newTag, setNewTag] = useState({ name: "", color: "blue" });

  useEffect(() => {
    if (open && user) {
      fetchTags();
    }
  }, [open, user, unaId]);

  const fetchTags = async () => {
    setLoading(true);
    try {
      const query = supabase
        .from("saved_tags")
        .select("*")
        .eq("user_id", user!.id)
        .order("usage_count", { ascending: false });

      if (unaId) {
        query.eq("una_id", unaId);
      }

      const { data, error } = await query;
      if (error) throw error;
      setTags(data || []);
    } catch (error) {
      console.error("Error fetching tags:", error);
      toast.error("Failed to load tags");
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = async () => {
    const trimmedName = newTag.name.trim();
    if (!trimmedName) {
      toast.error("Tag name is required");
      return;
    }

    if (trimmedName.length > 50) {
      toast.error("Tag name must be less than 50 characters");
      return;
    }

    try {
      const { error } = await supabase.from("saved_tags").insert({
        user_id: user!.id,
        una_id: unaId || null,
        tag_name: trimmedName,
        color: newTag.color,
        usage_count: 0,
      });

      if (error) {
        if (error.code === "23505") {
          toast.error("Tag already exists");
        } else {
          throw error;
        }
        return;
      }

      toast.success("Tag added successfully");
      setShowAddDialog(false);
      setNewTag({ name: "", color: "blue" });
      fetchTags();
    } catch (error) {
      console.error("Error adding tag:", error);
      toast.error("Failed to add tag");
    }
  };

  const handleUpdateTag = async () => {
    if (!selectedTag) return;

    const trimmedName = newTag.name.trim();
    if (!trimmedName) {
      toast.error("Tag name is required");
      return;
    }

    try {
      const { error } = await supabase
        .from("saved_tags")
        .update({
          tag_name: trimmedName,
          color: newTag.color,
        })
        .eq("id", selectedTag.id);

      if (error) throw error;

      toast.success("Tag updated successfully");
      setShowEditDialog(false);
      setSelectedTag(null);
      setNewTag({ name: "", color: "blue" });
      fetchTags();
    } catch (error) {
      console.error("Error updating tag:", error);
      toast.error("Failed to update tag");
    }
  };

  const handleDeleteTag = async () => {
    if (!selectedTag) return;

    try {
      const { error } = await supabase.from("saved_tags").delete().eq("id", selectedTag.id);

      if (error) throw error;

      toast.success("Tag deleted successfully");
      setShowDeleteDialog(false);
      setSelectedTag(null);
      fetchTags();
    } catch (error) {
      console.error("Error deleting tag:", error);
      toast.error("Failed to delete tag");
    }
  };

  const filteredTags = tags.filter((tag) =>
    tag.tag_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>🏷️ Manage Saved Tags</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Your saved tags for quick selection
            </p>
          </DialogHeader>

          <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Tag
              </Button>
            </div>

            <div className="flex-1 overflow-auto border rounded-md">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : filteredTags.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                  <p>No saved tags yet</p>
                  <p className="text-sm">Tags you create will appear here for quick reuse</p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredTags.map((tag) => (
                    <div
                      key={tag.id}
                      className="flex items-center justify-between p-3 hover:bg-accent/50"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <Badge variant="secondary">#{tag.tag_name}</Badge>
                        <span className="text-xs text-muted-foreground">
                          Used {tag.usage_count}×
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedTag(tag);
                            setNewTag({ name: tag.tag_name, color: tag.color });
                            setShowEditDialog(true);
                          }}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedTag(tag);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p className="text-sm text-muted-foreground">
              {filteredTags.length} saved tag{filteredTags.length !== 1 ? "s" : ""}
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Tag Dialog */}
      <Dialog open={showAddDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowAddDialog(false);
          setShowEditDialog(false);
          setNewTag({ name: "", color: "blue" });
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{showEditDialog ? "Edit Tag" : "Add New Tag"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="tagName">Tag Name *</Label>
              <Input
                id="tagName"
                value={newTag.name}
                onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                placeholder="urgent"
                maxLength={50}
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <RadioGroup
                value={newTag.color}
                onValueChange={(color) => setNewTag({ ...newTag, color })}
                className="grid grid-cols-2 gap-2"
              >
                {TAG_COLORS.map((color) => (
                  <div key={color.value} className="flex items-center space-x-2">
                    <RadioGroupItem value={color.value} id={color.value} />
                    <Label htmlFor={color.value} className="cursor-pointer">
                      {color.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddDialog(false);
                setShowEditDialog(false);
                setNewTag({ name: "", color: "blue" });
              }}
            >
              Cancel
            </Button>
            <Button onClick={showEditDialog ? handleUpdateTag : handleAddTag}>
              {showEditDialog ? "Update Tag" : "Add Tag"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>⚠️ Delete Tag?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the tag "#{selectedTag?.tag_name}"?
              <br />
              <br />
              This tag is used in {selectedTag?.usage_count} task{selectedTag?.usage_count !== 1 ? "s" : ""}.
              Tasks will keep the tag, but it won't appear in your saved list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTag}>Delete Tag</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
